import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Container, Card, CardBody, Row, Col, FormGroup, Label, Input,
    Button, Alert, Spinner, Table, Modal, ModalHeader, ModalBody, Collapse, Badge
} from "reactstrap";
import { Typeahead } from 'react-bootstrap-typeahead';
import AuthFunctions from '../../services/AuthService';
import Session from '../../auth/Session';
import { useNavigate, useParams } from 'react-router-dom';
import * as dayjs from 'dayjs';
import { getFinanceFilters, saveFinanceFilter } from "./financeFilterStorage";
import { WT_ADT_ENABLED } from "../../../featureFlags";

const isOvertimeRow = (detail) => detail?.detailType === 'Overtime';
// A flat "False Trip" driver-pay line, identified by its detailType.
const isFalseTripRow = (detail) => detail?.detailType === 'FalseTrip';
// A "Backup Driver" standby line, identified by its detailType.
const isBackupDriverRow = (detail) => detail?.detailType === 'BackupDriver';
// A backup "make-up" line (base − route pay for short coverage days).
const isBackupMakeupRow = (detail) => detail?.detailType === 'BackupMakeup';
// A "Lead Pay" management line, identified by its detailType.
const isLeadDriverRow = (detail) => detail?.detailType === 'LeadDriver';
const isSurchargeRow = (detail) => detail?.detailType === 'Surcharge' || detail?.surchargeId != null || detail?.surchargeTypeId != null;
// A manual one-off "Adjustment" pay line (description + qty + amount + transaction type).
const isAdjustmentRow = (detail) => detail?.detailType === 'Adjustment';
const isBaseRouteRow = (detail) => detail?.detailType === 'Route'
    || (detail?.surchargeId == null && detail?.surchargeTypeId == null && !isOvertimeRow(detail) && !isFalseTripRow(detail) && !isBackupDriverRow(detail) && !isBackupMakeupRow(detail) && !isLeadDriverRow(detail) && !isAdjustmentRow(detail));
const isPercentageSurchargeRow = (detail) => Number(detail?.surchargeTypeId ?? 0) === 2
    || `${detail?.surchargeTypeName ?? ""}`.toLowerCase() === "percentage";
const isFuelSurchargeRow = (detail) => Number(detail?.surchargeTypeId ?? 0) === 5
    || `${detail?.surchargeTypeName ?? ""}`.toLowerCase().includes("fuel");
const roundMoney = (val) => Math.round((Number(val ?? 0) + Number.EPSILON) * 100) / 100;

const calcDailyOt = (startTime, endTime, route) => {
    if (!route?.allowOvertime) return "";
    const cutoff = route.billingType === "Half-day"
        ? Number(route.halfDayOT ?? 0)
        : Number(route.fullDayOT ?? 0);
    if (cutoff <= 0) return "";
    const toHours = (t) => {
        if (!t) return null;
        const [h, m] = t.split(':').map(Number);
        if (isNaN(h) || isNaN(m)) return null;
        return h + m / 60;
    };
    const s = toHours(startTime);
    let e = toHours(endTime);
    if (s === null || e === null) return "";
    // An end time earlier than the start time means the shift ran past midnight into the
    // next day (e.g. PM start, AM end); roll the end forward 24h. Mirrors the backend
    // OvertimeCalculator.GetWorkedHours.
    if (e < s) e += 24;
    const total = e - s;
    if (total <= cutoff) return "";
    const rawOt = total - cutoff;
    if (route.roundOvertimeToHalfHour) {
        const increments = Math.round(Math.floor(rawOt / 0.5) * 0.5 * 2);
        return increments > 0 ? `${increments} × ½hr` : "";
    }
    return rawOt.toFixed(2);
};

const rowQuantity = (detail, isBaseRow, isOvertime, hoursFormatter) => {
    if (isBaseRow) {
        // Show the quantity at 2 decimals so hourly worked-hours read cleanly (7.62, not
        // 7.616666…), matching the Hours column and the invoice's lineQuantity formatter.
        return detail.billingType === "Hourly"
            ? `${hoursFormatter(detail.daysOnRoute)} hrs`
            : hoursFormatter(detail.daysOnRoute);
    }
    if (isOvertime) {
        if (detail?.roundOvertimeToHalfHour === true) {
            const increments = Math.round(Math.floor(Number(detail.daysOnRoute ?? 0) / 0.5) * 0.5 * 2);
            return increments > 0 ? `${increments} × ½hr` : hoursFormatter(detail.daysOnRoute);
        }
        return hoursFormatter(detail.daysOnRoute);
    }
    if (isPercentageSurchargeRow(detail)) return `${Number(detail?.surchargePercentage ?? 0).toFixed(2)}%`;
    return hoursFormatter(detail.daysOnRoute);
};
const getRouteId = (detail) => detail?.routeId ?? null;
const getRouteInfo = (detail, routeLookup) => {
    const routeId = getRouteId(detail);
    const lookupInfo = routeId != null ? routeLookup?.get(routeId) : null;
    return {
        id: routeId,
        location: detail?.location || lookupInfo?.location || '',
        routeName: detail?.routeName || lookupInfo?.routeName || ''
    };
};
const getRouteKey = (detail, routeLookup) => {
    const info = getRouteInfo(detail, routeLookup);
    return `${info.location}|${info.routeName}|${info.id ?? ''}`;
};
const getDisplayRows = (rows, routeLookup) => {
    const baseRouteRowIds = new Set();
    const firstRouteRowIds = new Set();
    let firstDriverRowId = null;

    for (const row of rows) {
        if (!isBaseRouteRow(row) || getRouteId(row) == null) {
            continue;
        }

        baseRouteRowIds.add(row.id);
        const routeKey = getRouteKey(row, routeLookup);
        if (!firstRouteRowIds.has(routeKey)) {
            firstRouteRowIds.add(routeKey);
        }

        if (firstDriverRowId == null) {
            firstDriverRowId = row.id;
        }
    }

    if (firstDriverRowId == null && rows.length > 0) {
        firstDriverRowId = rows[0].id;
    }

    return rows.map(row => ({
        detail: row,
        showDriverName: row.id === firstDriverRowId,
        showRouteInfo: baseRouteRowIds.has(row.id) && firstRouteRowIds.has(getRouteKey(row, routeLookup))
    }));
};
const getPaySheetDrivers = (data) => Array.isArray(data) ? data : (data?.drivers || []);
const getPaySheetDetails = (paySheet) => {
    const routeDetails = (paySheet?.routes || []).flatMap(route => {
        const routeRows = route?.details || [];
        const baseRows = routeRows.filter(isBaseRouteRow);
        const otherRows = routeRows.filter(row => !isBaseRouteRow(row));
        return [...baseRows, ...otherRows];
    });

    return [...routeDetails, ...(paySheet?.details || [])];
};

const PaySheetAudit = ({ readOnly = false }) => {
    const navigate = useNavigate();
    const { paySheetId: routePaySheetId } = useParams();
    const savedFilters = getFinanceFilters();
    const routeWeeksApi = "/api/routes/invoice-weeks";
    const loadPaySheetApi = (id) => `/api/paysheets/for-paysheet/${id}`;
    const approveApi = (paySheetId, createCheck) => `/api/paysheets/${paySheetId}/approve?createQboCheck=${createCheck ? "true" : "false"}`;
    const submitApi = (paySheetId) => `/api/paysheets/${paySheetId}/submit/false`;
    const dailyBreakdownApi = (paySheetId, detailId) => `/api/paysheets/${paySheetId}/detail/${detailId}/daily-breakdown`;

    const [drivers, setDrivers] = useState([]);
    const [driverId, setDriverId] = useState("");
    const [loading, setLoading] = useState(false);
    const [schedule, setSchedule] = useState(null);
    const [paySheetDrivers, setPaySheetDrivers] = useState([]);
    const [error, setError] = useState("");
    const [weeks, setWeeks] = useState([{ endingDate: '', displayDate: '' }]);
    const [selectedWeek, setSelectedWeek] = useState(savedFilters.selectedWeek || '');
    const [submitOpen, setSubmitOpen] = useState(false);
    const [approveOpen, setApproveOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [createQboCheck, setCreateQboCheck] = useState(true);
    const [qboPreview, setQboPreview] = useState(null);
    const [qboPreviewOpen, setQboPreviewOpen] = useState(false);
    const [qboPreviewBlocked, setQboPreviewBlocked] = useState(false);
    const [openRows, setOpenRows] = useState(new Set());
    const [detailsCache, setDetailsCache] = useState({});
    const [loadingDetails, setLoadingDetails] = useState({});
    const [actionLoading, setActionLoading] = useState("");

    useEffect(() => {
        if (readOnly) return;
        const loadData = async () => {
            const token = await Session.getToken();
            fetch(routeWeeksApi, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(setWeeks).catch(() => setWeeks([]));
        };
        loadData();
    }, [readOnly]);

    // Read-only single-paysheet load (approved-paysheet report "View" link). Fetches just this
    // paysheet's detail lines; the week-driven load below is skipped when readOnly.
    useEffect(() => {
        if (!readOnly || !routePaySheetId) return;
        const run = async () => {
            setLoading(true); setError("");
            try {
                const token = await Session.getToken();
                const res = await fetch(loadPaySheetApi(routePaySheetId), { headers: { Authorization: `Bearer ${token}` } });
                if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
                const data = await res.json();
                setSchedule(data.schedule);
                setPaySheetDrivers(getPaySheetDrivers(data));
                setOpenRows(new Set());
                setDetailsCache({});
            } catch (e) {
                setError(e.message || 'Failed to load pay sheet.');
            } finally {
                setLoading(false);
            }
        };
        run();
    }, [readOnly, routePaySheetId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const filters = getFinanceFilters();
        const selected = filters.selectedWeek
            ? weeks.find(w => w.endingDate === filters.selectedWeek)
            : weeks.find(w => w.selected === true);
        if (selected) {
            setSelectedWeek(selected.endingDate);
        }
    }, [weeks]);

    const loadPaySheetData = useCallback(async ({ showLoading = true } = {}) => {
        const isAuthenticated = await AuthFunctions.getAuthenticatedAsync();
        if (!isAuthenticated) {
            navigate('/logout');
            return;
        }

        if (!selectedWeek) return;

        if (showLoading) setLoading(true);
        setError("");

        try {
            const token = await Session.getToken();
            const payWeek = dayjs(selectedWeek, 'MM/DD/YYYY').format('YYYY-MM-DD');

            const [driversResponse, res] = await Promise.all([
                fetch(`/api/paysheets/drivers?endingDate=${payWeek}`, { headers: { Authorization: `Bearer ${token}` } }),
                fetch(`/api/paysheets/for-week?endingDate=${payWeek}&type=submitted`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            const driverData = driversResponse.ok ? await driversResponse.json() : [];
            setDrivers(driverData);

            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);

            const data = await res.json();
            setSchedule(data.schedule);
            setPaySheetDrivers(getPaySheetDrivers(data));
            setOpenRows(new Set());
            setDetailsCache({});

            setDriverId(currentDriverId =>
                currentDriverId && !driverData.some(d => `${d.id}` === `${currentDriverId}`)
                    ? ''
                    : currentDriverId
            );
        } catch (e) {
            setError(e.message || 'Failed to load pay sheets.');
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [navigate, selectedWeek]);

    useEffect(() => {
        if (readOnly) return;
        loadPaySheetData();
    }, [loadPaySheetData, readOnly]);

    const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "");
    const money = (n) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    // Fuel-surcharge rates are per-mile and need 3 decimals to read accurately (e.g. 0.119).
    const rateMoney = (detail) => {
        const digits = isFuelSurchargeRow(detail) ? 3 : 2;
        return (detail?.payRate ?? 0).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
    };
    const hours = (n) => (Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const formatAmPm = (time) => {
        if (!time) return "";
        const [h, m] = time.split(':').map(Number);
        if (isNaN(h) || isNaN(m)) return time;
        const period = h < 12 ? "AM" : "PM";
        const hour = h % 12 || 12;
        return `${hour}:${String(m).padStart(2, '0')} ${period}`;
    };

    const filteredPaySheetDrivers = useMemo(() => {
        if (!driverId) return [...paySheetDrivers];
        return paySheetDrivers.filter(d => `${d?.driverId}` === `${driverId}`);
    }, [paySheetDrivers, driverId]);

    const groupedPaySheets = useMemo(() => {
        return filteredPaySheetDrivers.flatMap(driver => (driver.paysheets || []).map(paySheet => ({
            paySheetId: paySheet.paySheetId,
            paySheet,
            driverName: driver.driverName || `Driver ${driver.driverId}`,
            routes: paySheet.routes || [],
            rows: getPaySheetDetails(paySheet)
        })))
            .map(group => {
                const routeLookup = new Map();
                for (const route of group.routes) {
                    const routeId = route?.routeId ?? null;
                    if (routeId == null) {
                        continue;
                    }
                    routeLookup.set(routeId, {
                        location: route.location || '',
                        routeName: route.routeName || ''
                    });
                }

                const displayTotal = group.rows.reduce((sum, row) => {
                    const lineVal = row.routeTotal != null
                        ? Number(row.routeTotal)
                        : (parseFloat(row.payRate) || 0) * (parseFloat(row.daysOnRoute) || 0);
                    return sum + roundMoney(lineVal);
                }, 0);

                return { ...group, routeLookup, displayTotal };
            })
            .sort((a, b) => a.driverName.localeCompare(b.driverName));
    }, [filteredPaySheetDrivers]);

    const openSubmit = (detail) => {
        setEditing(detail);
        setSubmitOpen(true);
    };

    const openApprove = (detail) => {
        setEditing(detail);
        setCreateQboCheck(true);
        setApproveOpen(true);
    };

    const handleSubmit = async () => {
        if (!editing) return;

        setActionLoading("review");
        try {
            const token = await Session.getToken();
            const res = await fetch(submitApi(editing.paySheetId), {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            await loadPaySheetData({ showLoading: false });
            setSubmitOpen(false);
            setApproveOpen(false);
        } catch (e) {
            setError(e.message || 'Failed to send pay sheet to review.');
        } finally {
            setActionLoading("");
        }
    };

    const handleApprove = async () => {
        if (!editing) return;

        setActionLoading("approve");
        try {
            const token = await Session.getToken();
            const res = await fetch(approveApi(editing.paySheetId, createQboCheck), {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            const data = await res.json();
            await loadPaySheetData({ showLoading: false });
            setApproveOpen(false);
            if (data?.qboPreview) {
                setQboPreview(data.qboPreview);
                setQboPreviewBlocked(!!data.validationBlocked);
                setQboPreviewOpen(true);
            }
            if (data?.validationBlocked) {
                setError("Pay sheet NOT approved — fix the QuickBooks vendor and/or account links, then approve again.");
            } else if (data?.qboPushError) {
                setError(`Pay sheet approved, but the QuickBooks check failed: ${data.qboPushError}`);
                setTimeout(() => setError(""), 8000);
            } else {
                setError("");
            }
        } catch (e) {
            setError(e.message || 'Failed to approve pay sheet.');
        } finally {
            setActionLoading("");
        }
    };

    const toggleRow = async (detail) => {
        if (!detail || !isBaseRouteRow(detail)) return;

        const rowKey = `${detail.paySheetId}:${detail.id}`;
        const next = new Set(openRows);

        if (next.has(rowKey)) {
            next.delete(rowKey);
            setOpenRows(next);
            return;
        }

        if (!detailsCache[rowKey]) {
            setLoadingDetails(prev => ({ ...prev, [rowKey]: true }));
            try {
                const token = await Session.getToken();
                const res = await fetch(dailyBreakdownApi(detail.paySheetId, detail.id), {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
                const rows = await res.json();
                setDetailsCache(prev => ({ ...prev, [rowKey]: rows }));
            } catch (e) {
                setError(e.message || 'Failed to load route detail.');
                setTimeout(() => setError(""), 4000);
            } finally {
                setLoadingDetails(prev => ({ ...prev, [rowKey]: false }));
            }
        }

        next.add(rowKey);
        setOpenRows(next);
    };

    return (
        <Container className="mt-0">
            <Card>
                <CardBody>
                    <Row className="mb-3">
                        <Col><h5>{readOnly ? "Approved Pay Sheet (Read-Only)" : "Pay Sheet Audit"}</h5></Col>
                    </Row>

                    {!readOnly && (
                    <Row className="mb-3">
                        <Col md={6}>
                            <Input
                                type="select"
                                value={selectedWeek}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setSelectedWeek(value);
                                    saveFinanceFilter("selectedWeek", value);
                                }}
                            >
                                <option value="">-- Select Week --</option>
                                {weeks.map(w => (
                                    <option key={w.endingDate} value={w.endingDate}>{w.displayDate}</option>
                                ))}
                            </Input>
                        </Col>
                        <Col md={6}>
                            <Typeahead
                                id="paysheet-audit-driver-filter"
                                labelKey="name"
                                options={drivers}
                                selected={drivers.filter(d => `${d.id}` === `${driverId}`)}
                                onChange={(selected) => setDriverId(selected.length ? selected[0].id : "")}
                                placeholder="-- Select Driver --"
                                clearButton
                            />
                        </Col>
                    </Row>
                    )}

                    {error && <Alert color="danger">{error}</Alert>}

                    {schedule && (
                        <Row className="mb-4">
                            <Col md={4}>
                                <Card className="shadow-sm">
                                    <CardBody>
                                        <div className="text-muted">Payment Week</div>
                                        <div className="h5 mb-0">{fmt(schedule.startDate)} - {fmt(schedule.endDate)}</div>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="shadow-sm">
                                    <CardBody>
                                        <div className="text-muted">Pay Sheet Date</div>
                                        <div className="h5 mb-0">{fmt(schedule.payDate)}</div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {loading ? (
                        <div className="text-center py-4"><Spinner /></div>
                    ) : (
                        <Table bordered hover responsive>
                            <thead className="table-light">
                                <tr>
                                    <th></th>
                                    <th>Driver Name</th>
                                    <th>Location</th>
                                    <th>Route Name</th>
                                    <th>Description</th>
                                    <th>Qty</th>
                                    <th>Mileage</th>
                                    <th>Hours</th>
                                    <th>Rate</th>
                                    <th>Line Total</th>
                                    <th>Pay Sheet Total</th>
                                    {!readOnly && <th></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {groupedPaySheets.length === 0 ? (
                                    <tr><td colSpan={readOnly ? 11 : 12} className="text-center">{readOnly ? "No detail lines found for this pay sheet." : "No pay sheets for the selected week."}</td></tr>
                                ) : (
                                    groupedPaySheets.map((group) => (
                                        getDisplayRows(group.rows, group.routeLookup).flatMap(({ detail, showDriverName, showRouteInfo }) => {
                                            const routeInfo = getRouteInfo(detail, group.routeLookup);
                                            const isBaseRow = isBaseRouteRow(detail);
                                            const isOvertime = isOvertimeRow(detail);
                                            const isAggregated = detail.surchargeId == null && detail.surchargeTypeId != null;
                                            const description = isOvertime
                                                ? 'Additional drive time'
                                                : (detail.surchargeId != null || isAggregated)
                                                    ? (detail.surchargeDescription || detail.surchargeTypeName || '')
                                                    : (detail.transactionName ?? '');
                                            const rowKey = `${detail.paySheetId}:${detail.id}`;
                                            const isOpen = openRows.has(rowKey);

                                            const mainRow = (
                                                <tr key={detail.id}>
                                                    <td className="text-center">
                                                        {isBaseRow && (
                                                            <Button size="sm" color="link" onClick={() => toggleRow(detail)}>
                                                                {isOpen ? '−' : '+'}
                                                            </Button>
                                                        )}
                                                    </td>
                                                    <td>{showDriverName ? group.driverName : ''}</td>
                                                    <td>{showRouteInfo ? routeInfo.location : ''}</td>
                                                    <td>{showRouteInfo ? routeInfo.routeName : ''}</td>
                                                    <td>{isFalseTripRow(detail) ? <Badge color="warning">False Trip</Badge> : isBackupDriverRow(detail) ? <Badge color="info">Backup Driver</Badge> : isBackupMakeupRow(detail) ? <Badge color="info">Backup Driver</Badge> : isLeadDriverRow(detail) ? <Badge color="primary">Lead Pay</Badge> : isAdjustmentRow(detail) ? <span><Badge color="secondary">Adjustment</Badge> {detail.description}</span> : description}</td>
                                                    <td style={{ textAlign: 'center' }}>{rowQuantity(detail, isBaseRow, isOvertime, hours)}</td>
                                                    <td style={{ textAlign: 'right' }}>{detail.totalMileage ?? 0}</td>
                                                    <td style={{ textAlign: 'right' }}>{isSurchargeRow(detail) ? '' : hours(detail.totalHoursWorked)}</td>
                                                    <td style={{ textAlign: 'right' }}>${rateMoney(detail)}</td>
                                                    <td style={{ textAlign: 'right' }}>${money(detail.routeTotal ?? ((parseFloat(detail.payRate) || 0) * (parseFloat(detail.daysOnRoute) || 0)))}</td>
                                                    <td style={{ textAlign: 'right' }}>{showDriverName ? `$${money(group.displayTotal)}` : ''}</td>
                                                    {!readOnly && (
                                                        <td className="text-center">
                                                            {showDriverName && (
                                                                <>
                                                                    <Button size="sm" color="success" onClick={() => openApprove(detail)} style={{ marginRight: 5 }}>
                                                                        Approve
                                                                    </Button>
                                                                    <Button size="sm" color="warning" onClick={() => openSubmit(detail)}>
                                                                        Review
                                                                    </Button>
                                                                </>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                            );

                                            const detailRow = isBaseRow ? (
                                                <tr key={`${detail.id}-detail`}>
                                                    <td colSpan={readOnly ? 11 : 12} className="p-0 border-top-0">
                                                        <Collapse isOpen={isOpen}>
                                                            <div className="p-3 bg-light">
                                                                {loadingDetails[rowKey] ? (
                                                                    <div className="text-center py-2"><Spinner size="sm" /></div>
                                                                ) : (
                                                                    <Table size="sm" bordered responsive className="mb-0">
                                                                        <thead>
                                                                            <tr>
                                                                                <th>Day</th>
                                                                                <th>Start Time</th>
                                                                                <th>End Time</th>
                                                                                <th>Hours Worked</th>
                                                                                {WT_ADT_ENABLED && <th title="Additional Drive Time">ADT</th>}
                                                                                <th>Mileage</th>
                                                                                <th>Days on Route</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {(detailsCache[rowKey] || []).length === 0 ? (
                                                                                <tr><td colSpan={WT_ADT_ENABLED ? 7 : 6} className="text-center">No daily route detail found.</td></tr>
                                                                            ) : (
                                                                                (detailsCache[rowKey] || []).map((item, itemIndex) => (
                                                                                    <tr key={`${rowKey}-${itemIndex}`}>
                                                                                        <td>{fmt(item.date)}</td>
                                                                                        <td>{formatAmPm(item.startTime)}</td>
                                                                                        <td>{formatAmPm(item.endTime)}</td>
                                                                                        <td style={{ textAlign: 'right' }}>{hours(item.hoursWorked)}</td>
                                                                                        {WT_ADT_ENABLED && <td style={{ textAlign: 'center' }}>{calcDailyOt(item.startTime, item.endTime, detail)}</td>}
                                                                                        <td style={{ textAlign: 'right' }}>{item.mileage ?? 0}</td>
                                                                                        <td style={{ textAlign: 'center' }}>{hours(item.daysOnRoute)}</td>
                                                                                    </tr>
                                                                                ))
                                                                            )}
                                                                        </tbody>
                                                                    </Table>
                                                                )}
                                                            </div>
                                                        </Collapse>
                                                    </td>
                                                </tr>
                                            ) : null;

                                            return detailRow ? [mainRow, detailRow] : [mainRow];
                                        })
                                    ))
                                )}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            {!readOnly && (
            <>
            <Modal isOpen={approveOpen} toggle={() => setApproveOpen(false)}>
                <ModalHeader toggle={() => setApproveOpen(false)}>Approve Pay Sheet</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>This will approve the selected pay sheet for payment, do you want to continue?</Label>
                    </FormGroup>
                    <FormGroup check className="mb-2">
                        <Input
                            type="checkbox"
                            id="approveCreateQboCheck"
                            checked={createQboCheck}
                            onChange={(e) => setCreateQboCheck(e.target.checked)}
                            disabled={!!actionLoading}
                        />
                        <Label check for="approveCreateQboCheck">Create the QuickBooks check</Label>
                    </FormGroup>
                    <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={() => setApproveOpen(false)} disabled={!!actionLoading}>Cancel</Button>
                        <Button color="warning" onClick={handleSubmit} disabled={!!actionLoading}>
                            {actionLoading === "review" ? <><Spinner size="sm" /> Sending...</> : 'Send to Review'}
                        </Button>
                        <Button color="primary" onClick={handleApprove} disabled={!!actionLoading}>
                            {actionLoading === "approve" ? <><Spinner size="sm" /> Approving...</> : 'Approve'}
                        </Button>
                    </div>
                </ModalBody>
            </Modal>
            <Modal isOpen={submitOpen} toggle={() => setSubmitOpen(false)}>
                <ModalHeader toggle={() => setSubmitOpen(false)}>Return to Review</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Alert color="warning" className="mb-0">
                            This will send the pay sheet back to be reviewed. Do you want to continue?
                        </Alert>
                    </FormGroup>
                    <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={() => setSubmitOpen(false)} disabled={!!actionLoading}>Cancel</Button>
                        <Button color="warning" onClick={handleSubmit} disabled={!!actionLoading}>
                            {actionLoading === "review" ? <><Spinner size="sm" /> Sending...</> : 'Send to Review'}
                        </Button>
                    </div>
                </ModalBody>
            </Modal>

            {/* QuickBooks Check Preview (grid) — shown instead of pushing when PreviewCheckPush is on,
                or when validation blocks the push */}
            <Modal isOpen={qboPreviewOpen} toggle={() => setQboPreviewOpen(false)} size="lg">
                <ModalHeader toggle={() => setQboPreviewOpen(false)}>
                    {qboPreviewBlocked ? "Cannot Create QuickBooks Check" : "QuickBooks Check Preview"}
                    {qboPreview?.docNumber ? ` — #${qboPreview.docNumber}` : ""}
                </ModalHeader>
                <ModalBody>
                    {qboPreviewBlocked ? (
                        <Alert color="danger" className="mb-3">
                            This pay sheet was <strong>not approved or pushed</strong> because it is missing a
                            QuickBooks vendor and/or one or more lines are not linked to a QuickBooks account.
                            Fix the highlighted items, then approve again.
                        </Alert>
                    ) : (
                        <Alert color="info" className="mb-3">
                            Preview mode is enabled, so this pay sheet was <strong>not</strong> pushed to QuickBooks.
                            Below is the line-by-line check data that would be sent.
                        </Alert>
                    )}
                    {qboPreview && (
                        <>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <div className="text-muted small">Vendor</div>
                                    <div>{qboPreview.vendorName || <span className="text-danger">— not linked —</span>}</div>
                                </Col>
                                <Col md={6}>
                                    <div className="text-muted small">Check Date</div>
                                    <div>{qboPreview.txnDate || "—"}</div>
                                </Col>
                            </Row>
                            {qboPreview.warnings && qboPreview.warnings.length > 0 && (
                                <Alert color="warning">
                                    <div className="fw-bold mb-1">Issues that would affect a live push:</div>
                                    <ul className="mb-0">
                                        {qboPreview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                    </ul>
                                </Alert>
                            )}
                            <Table bordered size="sm" responsive>
                                <thead className="table-light">
                                    <tr>
                                        <th>Description</th>
                                        <th>QB Account</th>
                                        <th>Class</th>
                                        <th className="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(qboPreview.lines || []).map((l, i) => (
                                        <tr key={i}>
                                            <td>{l.description}</td>
                                            <td>{l.accountName || <span className="text-danger">— not linked —</span>}</td>
                                            <td>{l.className || '—'}</td>
                                            <td className="text-end">${money(l.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="3" className="text-end fw-bold">Total</td>
                                        <td className="text-end fw-bold">${money(qboPreview.total)}</td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </>
                    )}
                    <div className="d-flex justify-content-end">
                        <Button color="primary" onClick={() => setQboPreviewOpen(false)}>Close</Button>
                    </div>
                </ModalBody>
            </Modal>
            </>
            )}
        </Container>
    );
};

export default PaySheetAudit;
